import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  getAllRetos,
  getRetosActivos,
  getRetosPopulares,
  getRetosDestacados,
  getRetosByPeriodo,
  getRetoById,
  getMiProgresoByRetoId,
  getMisRetosActivos,
  getMisRetosCompletados,
  retoPeriodoLabels,
  retoPeriodoIcons,
  retoPeriodoColors,
  recompensaConfig,
  retoScoreActions,
  retoLeaderboard,
  retosDashboard,
  miProgresoRetos,
  formatRetoCount,
  formatTiempoRestante,
  type RetoClub,
  type RetoPeriodo,
  type MiProgresoReto,
  type RetoRecompensa,
  type TipoRecompensa,
  type RetoLeaderboardEntry,
} from '@/mocks/rfwClubChallenges';

/* ─── Reto Card ─── */
function RetoCard({ reto, onClick }: { reto: RetoClub; onClick: () => void }) {
  const periodoConfig = retoPeriodoColors[reto.periodo];
  const tiempoRestante = formatTiempoRestante(reto.fechaFin);
  const porcentaje = reto.objetivo > 0 ? Math.round((reto.completados / reto.participantes) * 100) : 0;

  return (
    <div
      onClick={onClick}
      className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-background-300 transition-all group cursor-pointer"
    >
      {/* Top gradient bar */}
      <div className={`h-1.5 ${periodoConfig.bg}`}></div>
      <div className="p-4">
        {/* Badges row */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${periodoConfig.badge} ${periodoConfig.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${periodoConfig.dot}`}></span>
            {retoPeriodoLabels[reto.periodo]}
          </span>
          {reto.destacado && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
              <i className="ri-star-fill text-[8px]"></i>Destacado
            </span>
          )}
          {reto.popular && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-600 border border-red-200">
              <i className="ri-fire-fill text-[8px]"></i>Popular
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-sm font-extrabold text-foreground-950 mb-1 group-hover:text-accent-600 transition-colors">{reto.titulo}</h3>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-2">{reto.descripcion}</p>

        {/* Club + tiempo */}
        <div className="flex items-center justify-between mb-3 text-[10px]">
          <span className="text-foreground-400 flex items-center gap-1">
            <i className="ri-group-line text-[9px]"></i>
            {reto.clubNombre}
          </span>
          <span className={`font-semibold ${tiempoRestante === 'Finalizado' ? 'text-red-500' : 'text-foreground-600'}`}>
            <i className="ri-time-line text-[9px] mr-0.5"></i>
            {tiempoRestante}
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] text-foreground-400">Completado</span>
            <span className="text-[9px] font-bold text-foreground-700">{porcentaje}%</span>
          </div>
          <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${periodoConfig.bg}`}
              style={{ width: `${Math.min(porcentaje, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div className="text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{formatRetoCount(reto.participantes)}</span>
            <span className="text-[9px] text-foreground-400">Participantes</span>
          </div>
          <div className="text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{formatRetoCount(reto.completados)}</span>
            <span className="text-[9px] text-foreground-400">Completaron</span>
          </div>
          <div className="text-center">
            <span className="block text-sm font-extrabold text-accent-600">+{reto.scoreBase}</span>
            <span className="text-[9px] text-foreground-400">Pts Base</span>
          </div>
        </div>

        {/* Rewards mini */}
        <div className="flex flex-wrap gap-1">
          {reto.recompensas.slice(0, 3).map((rec, i) => {
            const cfg = recompensaConfig[rec.tipo];
            return (
              <span key={i} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[8px] font-semibold ${cfg.bg} ${cfg.color}`}>
                <i className={`${cfg.icon} text-[7px]`}></i>
                {cfg.label}
              </span>
            );
          })}
          {reto.recompensas.length > 3 && (
            <span className="text-[8px] text-foreground-400 font-medium px-1">+{reto.recompensas.length - 3} más</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Reto Detail Modal ─── */
function RetoDetailModal({ reto, onClose }: { reto: RetoClub; onClose: () => void }) {
  const [modalTab, setModalTab] = useState<'info' | 'progreso' | 'leaderboard'>('info');
  const periodoConfig = retoPeriodoColors[reto.periodo];
  const miProgreso = getMiProgresoByRetoId(reto.id);
  const tiempoRestante = formatTiempoRestante(reto.fechaFin);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`p-5 ${periodoConfig.bg} rounded-t-2xl relative`}>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/30 hover:bg-background-50/50 text-background-50 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm">
            <i className="ri-close-line"></i>
          </button>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-background-50/20 text-background-50 border border-background-50/20 backdrop-blur-sm">
              {retoPeriodoLabels[reto.periodo]}
            </span>
            {reto.destacado && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-background-50/20 text-background-50 backdrop-blur-sm">
                <i className="ri-star-fill text-[8px]"></i>Destacado
              </span>
            )}
            {reto.patrocinador && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-background-50/20 text-background-50 backdrop-blur-sm">
                <i className="ri-hand-coin-line text-[8px]"></i>Patrocinado
              </span>
            )}
          </div>
          <h2 className="text-xl font-extrabold text-background-50 dark:text-foreground-950 mb-1">{reto.titulo}</h2>
          <div className="flex items-center gap-3 text-background-50/70 text-xs">
            <span className="flex items-center gap-1"><i className="ri-group-line text-[10px]"></i>{reto.clubNombre}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line text-[10px]"></i>{tiempoRestante}</span>
            <span>{formatRetoCount(reto.participantes)} participantes</span>
          </div>
        </div>

        <div className="px-5 pb-6">
          {/* Descripción */}
          <div className="mt-5 mb-4">
            <h4 className="text-sm font-extrabold text-foreground-950 mb-1">Instrucciones</h4>
            <p className="text-sm text-foreground-600 leading-relaxed">{reto.instrucciones}</p>
          </div>

          {/* Patrocinador */}
          {reto.patrocinador && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-violet-50 border border-violet-100 mb-4">
              {reto.patrocinadorLogo && (
                <img src={reto.patrocinadorLogo} alt={reto.patrocinador} className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
              )}
              <div>
                <span className="text-[10px] text-violet-500 font-semibold">PATROCINADO POR</span>
                <p className="text-sm font-bold text-violet-800">{reto.patrocinador}</p>
              </div>
            </div>
          )}

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-2 mb-5">
            {[
              { label: 'Participantes', value: formatRetoCount(reto.participantes), icon: 'ri-user-line' },
              { label: 'Completaron', value: formatRetoCount(reto.completados), icon: 'ri-check-double-line' },
              { label: 'Score Base', value: `+${reto.scoreBase}`, icon: 'ri-flashlight-line' },
              { label: 'Objetivo', value: reto.objetivo.toString(), icon: 'ri-flag-line' },
            ].map((s) => (
              <div key={s.label} className="bg-background-100 rounded-xl p-2.5 text-center">
                <i className={`${s.icon} text-foreground-400 text-sm mb-1 block`}></i>
                <span className="block text-base font-extrabold text-foreground-950">{s.value}</span>
                <span className="text-[9px] text-foreground-400">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Modal Tabs */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-5 overflow-x-auto">
            {([
              { id: 'info' as const, label: 'Recompensas', icon: 'ri-gift-line' },
              { id: 'progreso' as const, label: 'Mi Progreso', icon: 'ri-user-line' },
              { id: 'leaderboard' as const, label: 'Top 10', icon: 'ri-medal-line' },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setModalTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  modalTab === tab.id ? 'bg-accent-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab: Recompensas */}
          {modalTab === 'info' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {reto.recompensas.map((rec, i) => {
                const cfg = recompensaConfig[rec.tipo];
                return (
                  <div key={i} className={`rounded-xl p-4 ${cfg.bg}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <i className={`${cfg.icon} ${cfg.color} text-lg`}></i>
                      <span className={`text-sm font-extrabold ${cfg.color}`}>{cfg.label}</span>
                    </div>
                    <p className="text-xs text-foreground-600 leading-relaxed">{rec.descripcion}</p>
                    {rec.valor && (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-bold text-accent-600">
                        <i className="ri-flashlight-line text-[9px]"></i>+{rec.valor} pts
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Tab: Mi Progreso */}
          {modalTab === 'progreso' && miProgreso && (
            <div className="space-y-4">
              {/* Mi Posición */}
              <div className="bg-background-100 rounded-2xl p-5">
                <h4 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-medal-line text-amber-500"></i>
                  Mi Posición
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <span className="block text-2xl font-extrabold text-foreground-950">#{miProgreso.posicion}</span>
                    <span className="text-[10px] text-foreground-400">de {miProgreso.totalParticipantes}</span>
                  </div>
                  <div className="text-center">
                    <span className={`block text-2xl font-extrabold ${miProgreso.estado === 'completado' ? 'text-emerald-600' : 'text-accent-600'}`}>
                      {miProgreso.porcentaje}%
                    </span>
                    <span className="text-[10px] text-foreground-400">Completado</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-2xl font-extrabold text-foreground-950">{miProgreso.progreso}/{miProgreso.objetivo}</span>
                    <span className="text-[10px] text-foreground-400">Progreso</span>
                  </div>
                </div>
              </div>

              {/* Barra de progreso */}
              <div className="bg-background-100 rounded-2xl p-5">
                <h4 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-accent-500"></i>
                  Mi Progreso
                </h4>
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-foreground-700">
                      {miProgreso.estado === 'completado' ? 'Completado' : 'En progreso'}
                    </span>
                    <span className="text-xs font-bold text-foreground-700">{miProgreso.porcentaje}%</span>
                  </div>
                  <div className="w-full h-3 bg-background-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${miProgreso.estado === 'completado' ? 'bg-emerald-500' : periodoConfig.bg}`}
                      style={{ width: `${miProgreso.porcentaje}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">{miProgreso.progreso} de {miProgreso.objetivo} completado{miProgreso.objetivo > 1 ? 's' : ''}</span>
                  <span className="font-semibold text-foreground-600 flex items-center gap-1">
                    <i className="ri-time-line text-[10px]"></i>
                    {miProgreso.tiempoRestante}
                  </span>
                </div>
                {miProgreso.estado === 'completado' && miProgreso.fechaCompletado && (
                  <div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-emerald-50 text-emerald-700">
                    <i className="ri-check-double-line text-sm"></i>
                    <span className="text-[11px] font-semibold">Completado el {new Date(miProgreso.fechaCompletado).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {modalTab === 'progreso' && !miProgreso && (
            <div className="text-center py-8 text-foreground-400">
              <i className="ri-user-line text-2xl mb-2 block"></i>
              <p className="text-xs">No estás participando en este reto todavía</p>
              <button className="mt-3 flex items-center gap-1.5 mx-auto px-4 py-2 bg-accent-500 text-background-50 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap">
                <i className="ri-sword-line text-[11px]"></i>
                Empezar Reto
              </button>
            </div>
          )}

          {/* Tab: Leaderboard */}
          {modalTab === 'leaderboard' && (
            <div>
              <p className="text-xs text-foreground-500 mb-3">Top 10 participantes de este reto</p>
              <div className="space-y-2">
                {retoLeaderboard.map((entry, i) => (
                  <div key={entry.posicion} className={`flex items-center gap-3 bg-background-100 rounded-xl p-3 ${entry.posicion <= 3 ? 'border border-amber-200/60' : ''}`}>
                    <div className="shrink-0 w-7 text-center">
                      {entry.posicion === 1 ? <span className="text-base">🥇</span>
                       : entry.posicion === 2 ? <span className="text-base">🥈</span>
                       : entry.posicion === 3 ? <span className="text-base">🥉</span>
                       : <span className="text-xs font-extrabold text-foreground-400">{entry.posicion}</span>
                      }
                    </div>
                    <img src={entry.avatarUrl} alt={entry.nombre} className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground-950 truncate">{entry.nombre}</p>
                      <span className="text-[10px] text-foreground-400">{entry.progreso}/{entry.objetivo} · {entry.completado ? 'Completado' : 'En progreso'}</span>
                    </div>
                    <div className="shrink-0 text-right">
                      {entry.completado ? (
                        <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                          <i className="ri-check-double-line text-[9px]"></i>
                          {entry.tiempoTotal || 'Completado'}
                        </span>
                      ) : (
                        <span className="text-xs font-bold text-foreground-700">{Math.round((entry.progreso / entry.objetivo) * 100)}%</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Mi Progreso Card ─── */
function MiProgresoCard({ progreso, reto }: { progreso: MiProgresoReto; reto: RetoClub | undefined }) {
  const periodoConfig = reto ? retoPeriodoColors[reto.periodo] : { bg: 'bg-accent-500' };

  return (
    <div className="bg-background-50 rounded-xl p-4 border border-background-200/70 hover:border-background-300 transition-all cursor-pointer">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${progreso.estado === 'completado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {progreso.estado === 'completado' ? 'Completado' : 'En Progreso'}
        </span>
        <span className="text-[10px] font-semibold text-foreground-500 flex items-center gap-1">
          <i className="ri-time-line text-[9px]"></i>
          {progreso.tiempoRestante}
        </span>
      </div>
      <p className="text-sm font-bold text-foreground-950 mb-2">{reto?.titulo || `Reto #${progreso.retoId}`}</p>
      {reto && (
        <span className="text-[10px] text-foreground-400 mb-2 block">{reto.clubNombre}</span>
      )}

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-foreground-500">Progreso</span>
          <span className="text-[10px] font-bold text-foreground-700">{progreso.porcentaje}%</span>
        </div>
        <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${progreso.estado === 'completado' ? 'bg-emerald-500' : periodoConfig.bg}`}
            style={{ width: `${progreso.porcentaje}%` }}
          ></div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="text-center bg-background-100 rounded-lg p-1.5">
          <span className="block text-sm font-extrabold text-foreground-950">#{progreso.posicion}</span>
          <span className="text-[8px] text-foreground-400">de {progreso.totalParticipantes}</span>
        </div>
        <div className="text-center bg-background-100 rounded-lg p-1.5">
          <span className="block text-sm font-extrabold text-foreground-950">{progreso.progreso}/{progreso.objetivo}</span>
          <span className="text-[8px] text-foreground-400">Completado</span>
        </div>
      </div>
    </div>
  );
}

/* ─── PAGE ─── */
export default function RetosClubesPage() {
  const [periodoFilter, setPeriodoFilter] = useState<RetoPeriodo | 'todos'>('todos');
  const [vista, setVista] = useState<'retos' | 'mi-progreso' | 'recompensas'>('retos');
  const [selectedReto, setSelectedReto] = useState<RetoClub | null>(null);

  const filteredRetos = useMemo(() => {
    return periodoFilter === 'todos' ? getAllRetos() : getRetosByPeriodo(periodoFilter);
  }, [periodoFilter]);

  const misActivos = useMemo(() => getMisRetosActivos(), []);
  const misCompletados = useMemo(() => getMisRetosCompletados(), []);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">

          {/* HERO */}
          <div className="relative rounded-2xl overflow-hidden mb-8">
            <div className="h-56 md:h-64 relative">
              <img
                src="https://readdy.ai/api/search-image?query=Energetic%20community%20challenge%20atmosphere%20with%20people%20completing%20fun%20tasks%20together%2C%20vibrant%20warm%20orange%20and%20golden%20lighting%2C%20gamified%20club%20activities%2C%20celebration%20moments%20with%20confetti%20and%20trophies%2C%20modern%20community%20center%20aesthetic%2C%20editorial%20photography%2C%20dynamic%20wide%20angle%20shot%20with%20warm%20tones%20and%20joyful%20competitive%20energy&width=1600&height=900&seq=retos-clubes-hero-v1&orientation=landscape"
                alt="Retos de Clubes RFW"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center w-full">
                <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
                  <i className="ri-sword-fill text-3xl text-orange-400"></i>
                </div>
                <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
                  Retos de Clubes
                </h1>
                <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
                  Retos diarios, semanales, mensuales, especiales y patrocinados. Completalos, ganá puntos, insignias, premios y mantené viva tu comunidad.
                </p>
                <div className="flex items-center gap-3 mt-5 flex-wrap justify-center">
                  <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                    {retosDashboard.totalRetos} retos
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                    {formatRetoCount(retosDashboard.participantesTotales)}+ participantes
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                    {retosDashboard.activosAhora} activos ahora
                  </span>
                </div>
              </div>
            </div>

            {/* Stats bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-background-200 bg-background-50">
              {[
                { label: 'Retos Totales', value: retosDashboard.totalRetos.toString(), icon: 'ri-sword-line', color: 'text-orange-600' },
                { label: 'Participantes', value: formatRetoCount(retosDashboard.participantesTotales), icon: 'ri-user-line', color: 'text-emerald-600' },
                { label: 'Score Generado', value: formatRetoCount(retosDashboard.scoreGenerado), icon: 'ri-flashlight-line', color: 'text-accent-600' },
                { label: 'Patrocinadores', value: retosDashboard.patrocinadoresActivos.toString(), icon: 'ri-hand-coin-line', color: 'text-violet-600' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-lg font-extrabold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* VIEW TABS */}
          <div className="flex items-center gap-2 mb-6">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { id: 'retos' as const, label: 'Retos', icon: 'ri-sword-line', count: retosDashboard.totalRetos },
                { id: 'mi-progreso' as const, label: 'Mi Progreso', icon: 'ri-user-line', count: miProgresoRetos.length },
                { id: 'recompensas' as const, label: 'Recompensas', icon: 'ri-gift-line', count: 5 },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setVista(tab.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    vista === tab.id
                      ? 'bg-accent-500 text-background-50 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${vista === tab.id ? 'bg-background-50/20' : 'bg-background-200'}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* VISTA: RETOS */}
          {vista === 'retos' && (
            <>
              {/* Periodo filters */}
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto mb-6 flex-wrap">
                {([
                  { key: 'todos' as const, label: 'Todos', icon: 'ri-apps-line' },
                  { key: 'diario' as const, label: 'Diarios', icon: 'ri-sun-line' },
                  { key: 'semanal' as const, label: 'Semanales', icon: 'ri-calendar-check-line' },
                  { key: 'mensual' as const, label: 'Mensuales', icon: 'ri-calendar-2-line' },
                  { key: 'especial' as const, label: 'Especiales', icon: 'ri-star-line' },
                  { key: 'patrocinado' as const, label: 'Patrocinados', icon: 'ri-hand-coin-line' },
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setPeriodoFilter(tab.key)}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      periodoFilter === tab.key
                        ? 'bg-accent-500 text-background-50 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${tab.icon} text-sm`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredRetos.map((reto) => (
                  <RetoCard key={reto.id} reto={reto} onClick={() => setSelectedReto(reto)} />
                ))}
              </div>
            </>
          )}

          {/* VISTA: MI PROGRESO */}
          {vista === 'mi-progreso' && (
            <div className="space-y-8">
              {/* Activos */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <i className="ri-sword-line text-sm text-amber-600"></i>
                  </div>
                  <h2 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">En Progreso</h2>
                  <span className="text-[10px] text-foreground-400">({misActivos.length} retos)</span>
                </div>
                {misActivos.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {misActivos.map((p) => (
                      <MiProgresoCard key={p.retoId} progreso={p} reto={getRetoById(p.retoId)} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-background-100 rounded-2xl text-foreground-400">
                    <i className="ri-sword-line text-2xl mb-2 block"></i>
                    <p className="text-xs">No tenés retos en progreso</p>
                  </div>
                )}
              </div>

              {/* Completados */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <i className="ri-check-double-line text-sm text-emerald-600"></i>
                  </div>
                  <h2 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">Completados</h2>
                  <span className="text-[10px] text-foreground-400">({misCompletados.length} retos)</span>
                </div>
                {misCompletados.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {misCompletados.map((p) => (
                      <MiProgresoCard key={p.retoId} progreso={p} reto={getRetoById(p.retoId)} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-background-100 rounded-2xl text-foreground-400">
                    <i className="ri-check-double-line text-2xl mb-2 block"></i>
                    <p className="text-xs">Todavía no completaste ningún reto</p>
                  </div>
                )}
              </div>

              {/* Leaderboard */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <i className="ri-medal-line text-sm text-amber-600"></i>
                  </div>
                  <h2 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">Top 10 Global de Retos</h2>
                </div>
                <div className="bg-background-100 rounded-2xl p-4 space-y-2">
                  {retoLeaderboard.map((entry) => (
                    <div key={entry.posicion} className={`flex items-center gap-3 bg-background-50 rounded-xl p-3 ${entry.posicion <= 3 ? 'border border-amber-200/60' : ''}`}>
                      <div className="shrink-0 w-7 text-center">
                        {entry.posicion === 1 ? <span className="text-base">🥇</span>
                         : entry.posicion === 2 ? <span className="text-base">🥈</span>
                         : entry.posicion === 3 ? <span className="text-base">🥉</span>
                         : <span className="text-xs font-extrabold text-foreground-400">{entry.posicion}</span>
                        }
                      </div>
                      <img src={entry.avatarUrl} alt={entry.nombre} className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-foreground-950 truncate">{entry.nombre}</p>
                        <span className="text-[10px] text-foreground-400">{entry.progreso}/{entry.objetivo} · {entry.completado ? 'Completado' : 'En progreso'}</span>
                      </div>
                      <div className="shrink-0 text-right">
                        {entry.completado ? (
                          <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1">
                            <i className="ri-check-double-line text-[9px]"></i>
                            {entry.tiempoTotal || 'Completado'}
                          </span>
                        ) : (
                          <span className="text-xs font-bold text-foreground-700">{Math.round((entry.progreso / entry.objetivo) * 100)}%</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* VISTA: RECOMPENSAS */}
          {vista === 'recompensas' && (
            <div className="space-y-8">
              {/* Tipos de recompensa */}
              <div>
                <h2 className="text-sm font-extrabold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-gift-line text-rose-500"></i>
                  Tipos de Recompensa
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                  {([
                    { tipo: 'puntos' as TipoRecompensa, label: 'Puntos RFW', icon: 'ri-flashlight-line', color: 'text-accent-600', bg: 'bg-accent-100', desc: 'Sumá puntos a tu Score RFW por cada reto completado. Los puntos varían según la dificultad del reto.' },
                    { tipo: 'insignia' as TipoRecompensa, label: 'Insignias', icon: 'ri-award-line', color: 'text-violet-600', bg: 'bg-violet-100', desc: 'Insignias exclusivas que muestran tus logros en el perfil. Algunas son temporales, otras permanentes.' },
                    { tipo: 'vip' as TipoRecompensa, label: 'VIP', icon: 'ri-vip-crown-line', color: 'text-amber-600', bg: 'bg-amber-100', desc: 'Acceso VIP temporal o permanente. Beneficios premium, contenido exclusivo y acceso anticipado.' },
                    { tipo: 'premio' as TipoRecompensa, label: 'Premios', icon: 'ri-gift-line', color: 'text-rose-600', bg: 'bg-rose-100', desc: 'Premios físicos y digitales: merchandising, equipos, gift cards, productos de patrocinadores.' },
                    { tipo: 'entrada' as TipoRecompensa, label: 'Entradas', icon: 'ri-ticket-line', color: 'text-emerald-600', bg: 'bg-emerald-100', desc: 'Entradas a eventos, conciertos, meet & greets y experiencias exclusivas del club.' },
                  ]).map((rec) => (
                    <div key={rec.tipo} className={`rounded-xl p-4 ${rec.bg}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <i className={`${rec.icon} ${rec.color} text-xl`}></i>
                        <span className={`text-sm font-extrabold ${rec.color}`}>{rec.label}</span>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed">{rec.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Score Actions */}
              <div>
                <h2 className="text-sm font-extrabold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-flashlight-line text-accent-500"></i>
                  ¿Cómo sumás Score con los Retos?
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {retoScoreActions.map((action) => (
                    <div key={action.key} className={`flex items-center gap-3 p-4 rounded-xl ${action.bg}`}>
                      <i className={`${action.icon} ${action.color} text-lg`}></i>
                      <div>
                        <p className="text-sm font-bold text-foreground-950">{action.label}</p>
                        <p className="text-[11px] text-foreground-500">{action.desc}</p>
                        <p className="text-xs font-extrabold text-emerald-600 mt-1">+{action.puntos} pts</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CROSS LINKS */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Link to="/clubes" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-group-line text-primary-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Clubes de Fans</h4>
                  <p className="text-xs text-foreground-500">Tu comunidad</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/rankings" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-bar-chart-2-line text-accent-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Rankings RFW</h4>
                  <p className="text-xs text-foreground-500">Posiciones</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/logros" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <i className="ri-award-line text-violet-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Sistema de Logros</h4>
                  <p className="text-xs text-foreground-500">Tus insignias</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/embajadores" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <i className="ri-user-heart-line text-emerald-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Embajadores</h4>
                  <p className="text-xs text-foreground-500">Promotores oficiales</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

          {/* Regla Final */}
          <div className="mt-10 p-6 rounded-2xl bg-orange-50 border border-orange-100 text-center">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-sword-line text-lg text-background-50"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">
              Los retos deben ser simples y divertidos.
            </p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              Comentar publicaciones, compartir contenido, invitar amigos, mantener la racha, participar en cabina. Cada reto está diseñado para que cualquier miembro del club pueda completarlo y mantenerse activo. Lo simple funciona mejor que lo complicado. Lo divertido genera más engagement que lo obligatorio.
            </p>
          </div>

        </div>
      </main>

      <Footer />

      <div className="md:hidden h-20"></div>

      {selectedReto && (
        <RetoDetailModal reto={selectedReto} onClose={() => setSelectedReto(null)} />
      )}
    </div>
  );
}